import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { HorizontalLoader } from 'Components'
import { handleError, handleSuccess } from 'Helpers'
import { WithdrawalDetailsXModel } from 'BoostXpressAdmin/Model/payoutXModel'
import { payoutBXService } from 'BoostXpressAdmin/Services/payoutBXService'
import { ToastContainer } from 'react-toastify'
import backArrow from '../../../../../Assets/Icon/Users/back-arrow-detailed.svg'
import breadcrumbArrow from '../../../../../Assets/Icon/Referrals/breadcrumb-arrow.svg'
import './PayoutDetails.scss'

const STATUS_OPTIONS = ['PENDING', 'APPROVED', 'COMPLETED', 'REJECTED', 'FAILED']

const formatCurrency = (amount?: number | null) => `₦${Number(amount ?? 0).toLocaleString()}`

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const displayName = (withdrawal?: WithdrawalDetailsXModel | null) =>
  `${withdrawal?.user?.firstName ?? ''} ${withdrawal?.user?.lastName ?? ''}`.trim() || 'Withdrawal'

const PayoutDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [withdrawal, setWithdrawal] = useState<WithdrawalDetailsXModel | null>(null)
  const [statusDraft, setStatusDraft] = useState('PENDING')
  const [adminNote, setAdminNote] = useState('')

  const loadWithdrawal = async () => {
    if (!id) return

    try {
      setIsLoading(true)
      const res = await payoutBXService.showWithdrawal(id)
      const nextWithdrawal = res.data?.data ?? null
      setWithdrawal(nextWithdrawal)
      setStatusDraft(nextWithdrawal?.status ?? 'PENDING')
      setAdminNote(nextWithdrawal?.adminNote ?? '')
    } catch (error) {
      setWithdrawal(null)
      handleError(error as any)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadWithdrawal()
  }, [id])

  const handleUpdateStatus = async () => {
    if (!id) return

    try {
      setIsSubmitting(true)
      const res = await payoutBXService.updateWithdrawalStatus(id, {
        status: statusDraft,
        adminNote,
      })
      const updatedWithdrawal =
        res.data?.data ??
        (withdrawal
          ? {
              ...withdrawal,
              status: statusDraft,
              adminNote,
            }
          : null)

      setWithdrawal(updatedWithdrawal)
      handleSuccess('Withdrawal status updated successfully.')
    } catch (error) {
      handleError(error as any)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='bx-payout-details'>
      <ToastContainer />

      <div className='bx-payout-details__breadcrumb'>
        <button type='button' className='bx-payout-details__back' onClick={() => navigate(-1)}>
          <img src={backArrow} alt='Back' />
        </button>

        <div className='bx-payout-details__crumbs'>
          <Link to='/boostxpress/payouts' className='bx-payout-details__crumb-link'>
            Payouts
          </Link>
          <img src={breadcrumbArrow} alt='' />
          <span className='bx-payout-details__crumb-current'>Withdrawal Details</span>
        </div>
      </div>

      <div className='bx-payout-details__heading'>
        <h2>{isLoading ? 'Loading...' : displayName(withdrawal)}</h2>
      </div>

      {isLoading ? (
        <div className='bx-payout-details__loader'>
          <HorizontalLoader />
        </div>
      ) : (
        <div className='row g-4'>
          <div className='col-12 col-xl-7'>
            <section className='bx-payout-details__section'>
              <h3 className='bx-payout-details__section-title'>Withdrawal Information</h3>
              <div className='bx-payout-details__card'>
                <div className='bx-payout-details__row'>
                  <span>Attendant Name</span>
                  <strong>{displayName(withdrawal)}</strong>
                </div>
                <div className='bx-payout-details__row'>
                  <span>Email</span>
                  <strong>{withdrawal?.user?.email || '-'}</strong>
                </div>
                <div className='bx-payout-details__row'>
                  <span>User Resource ID</span>
                  <strong>{withdrawal?.user?.resourceId || '-'}</strong>
                </div>
                <div className='bx-payout-details__row'>
                  <span>Amount</span>
                  <strong>{formatCurrency(withdrawal?.amountMajor)}</strong>
                </div>
                <div className='bx-payout-details__row'>
                  <span>Status</span>
                  <strong>{withdrawal?.status || '-'}</strong>
                </div>
                <div className='bx-payout-details__row'>
                  <span>Processed At</span>
                  <strong>{formatDateTime(withdrawal?.processedAt)}</strong>
                </div>
                <div className='bx-payout-details__row'>
                  <span>Created At</span>
                  <strong>{formatDateTime(withdrawal?.createdAt)}</strong>
                </div>
              </div>
            </section>
          </div>

          <div className='col-12 col-xl-5'>
            <section className='bx-payout-details__section'>
              <h3 className='bx-payout-details__section-title'>Bank Details</h3>
              <div className='bx-payout-details__card'>
                <div className='bx-payout-details__row'>
                  <span>Bank Name</span>
                  <strong>{withdrawal?.bankInfoSnapshot?.bankName || '-'}</strong>
                </div>
                <div className='bx-payout-details__row'>
                  <span>Bank Code</span>
                  <strong>{withdrawal?.bankInfoSnapshot?.bankCode || '-'}</strong>
                </div>
                <div className='bx-payout-details__row'>
                  <span>Account Number</span>
                  <strong>{withdrawal?.bankInfoSnapshot?.bankAccountNumber || '-'}</strong>
                </div>
                <div className='bx-payout-details__row'>
                  <span>Account Name</span>
                  <strong>{withdrawal?.bankInfoSnapshot?.bankAccountName || '-'}</strong>
                </div>
              </div>
            </section>
          </div>

          <div className='col-12'>
            <section className='bx-payout-details__section'>
              <h3 className='bx-payout-details__section-title'>Update Withdrawal Status</h3>
              <div className='bx-payout-details__card bx-payout-details__card--form'>
                <div className='row g-3'>
                  <div className='col-12 col-md-4'>
                    <label className='bx-payout-details__field'>
                      <span>Status</span>
                      <select
                        className='form-control'
                        value={statusDraft}
                        onChange={(e) => setStatusDraft(e.target.value)}
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className='col-12 col-md-8'>
                    <label className='bx-payout-details__field'>
                      <span>Admin Note</span>
                      <textarea
                        className='form-control'
                        rows={5}
                        value={adminNote}
                        onChange={(e) => setAdminNote(e.target.value)}
                      />
                    </label>
                  </div>
                </div>

                <div className='bx-payout-details__actions'>
                  <button type='button' className='bx-payout-details__primary' disabled={isSubmitting} onClick={handleUpdateStatus}>
                    {isSubmitting ? 'Saving...' : 'Update Status'}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  )
}

export default PayoutDetails

